import { useMemo, useState, useEffect } from 'react';
import { useMaestroStore, useDesktopMode } from '../store/maestroStore';
import { useTranslation } from '../i18n/useTranslation';
import { isIOSNativeApp } from '../utils/platform';

/**
 * Sidebar configuration hook.
 *
 * Returns the full sidebar section/item structure based on:
 *  - desktopMode ('standard' | 'full')
 *  - which services are active (enabled in settings)
 *  - which services are connected (authenticated)
 *
 * Only services with `active === true` appear in the sidebar.
 * Qobuz is always shown when active — if not connected it runs
 * in sample mode (30-second previews, yellow dot).
 *
 * To change what appears in the sidebar, edit THIS file.
 */

const ALL_SERVICES = [
  { id: 'qobuz', label: 'Qobuz', type: 'service' },
  { id: 'prestomusic', label: 'Presto Music', type: 'service' },
  { id: 'ella_radio', label: 'Ella Radio', type: 'service' },
  { id: 'live_radio', label: 'Live Radio', type: 'service' },
  { id: 'octave_radio', label: 'Octave Radio', type: 'service' },
];

export default function useSidebarConfig({ services }) {
  const desktopMode = useDesktopMode();
  const isFullMode = desktopMode === 'full';
  const { t, lang } = useTranslation();
  const isHomeLibraryHost = useMaestroStore((s) => s.isHomeLibraryHost);
  const [hostProbed, setHostProbed] = useState(false);
  const [remoteHomeReady, setRemoteHomeReady] = useState(false);

  useEffect(() => {
    const onProbed = () => setHostProbed(true);
    const syncRemote = () => {
      import('../services/localLibraryIpcGate').then((m) => {
        if (m.isLibraryHostProbeComplete()) setHostProbed(true);
        setRemoteHomeReady(m.isRemoteLibraryViewer());
      }).catch(() => {});
    };
    window.addEventListener('maestro:library-host-probed', onProbed);
    window.addEventListener('maestro:remote-home-library-changed', syncRemote);
    syncRemote();
    return () => {
      window.removeEventListener('maestro:library-host-probed', onProbed);
      window.removeEventListener('maestro:remote-home-library-changed', syncRemote);
    };
  }, []);

  const svc = services || {};
  const hasStreaming = ['qobuz', 'prestomusic']
    .some(id => svc[id]?.connected === true);

  const config = useMemo(() => {
    const topNav = {
      id: 'top_nav',
      label: '',
      items: [
        { id: 'home', label: t('nav.home') },
      ],
    };

    const discoverItems = [
      { id: 'valet_trending', label: t('nav.trending'), visible: hasStreaming },
      { id: 'valet_new_releases', label: t('nav.new_releases'), visible: hasStreaming },
      { id: 'genres', label: t('nav.genres') },
      { id: 'valet_community', label: t('nav.community') },
    ].filter(item => item.visible !== false);

    const discoverSection = {
      id: 'discover',
      label: t('nav.discover'),
      items: discoverItems,
    };

    const activeServiceItems = ALL_SERVICES.filter(s => svc[s.id]?.active === true);

    const servicesSection = {
      id: 'services',
      label: t('nav.services'),
      items: activeServiceItems,
    };

    const playlistsSection = {
      id: 'playlists',
      label: t('nav.playlists'),
      subsections: ['audiophile_playlists'],
    };

    const myMusicSection = {
      id: 'my_music',
      label: t('nav.my_music'),
      items: [
        { id: 'stream_albums', label: t('nav.lib_albums') },
        { id: 'stream_artists', label: t('nav.lib_artists') },
        { id: 'favorites', label: t('nav.favorites') },
        { id: 'history', label: t('nav.recently_played') },
      ],
    };

    const isiOS = isIOSNativeApp();
    const isTauri = typeof window !== 'undefined' && !!window.__TAURI__;
    const remoteViewer = isTauri && hostProbed && !isHomeLibraryHost && remoteHomeReady;
    const localFilesItems = [
      { id: 'lib_albums', label: t('nav.lib_albums'), localLibraryTab: 'albums' },
      { id: 'lib_artists', label: t('nav.lib_artists'), localLibraryTab: 'artists' },
      { id: 'lib_genres', label: t('nav.genres'), localLibraryTab: 'genres' },
      !isiOS && !remoteViewer && { id: 'lib_add_music', label: 'Add Music', localLibraryTab: 'manage' },
      !isiOS && !remoteViewer && { id: 'lib_manage', label: t('nav.manage'), localLibraryTab: 'manage' },
    ].filter(Boolean);

    const localFilesSection = isFullMode ? {
      id: 'local_files',
      label: t('nav.local_files'),
      items: localFilesItems,
    } : null;

    const toolsSection = {
      id: 'tools',
      label: t('nav.tools'),
      items: [
        { id: 'devices', label: t('nav.devices') },
        { id: 'prism', label: t('nav.prism') },
        { id: 'valet_personalize', label: t('nav.personalize') },
        { id: 'settings', label: t('nav.settings'), type: 'action' },
        { id: 'support', label: t('nav.support') },
        { id: 'account', label: 'Account' },
      ],
    };

    const sections = [
      topNav,
      discoverSection,
      servicesSection,
      playlistsSection,
      myMusicSection,
      localFilesSection,
      toolsSection,
    ].filter(Boolean);

    return { sections, isFullMode, hasStreaming };
  }, [isFullMode, hasStreaming, svc, t, lang, hostProbed, isHomeLibraryHost, remoteHomeReady]);

  return config;
}
